import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../i18n/translations';
import { 
  LayoutDashboard, 
  Home, 
  Calendar, 
  MessageSquare, 
  Settings, 
  Shield, 
  LogOut,
  Menu,
  X,
  Globe
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const MainLayout = ({ children }) => {
  const { user, logout, language, setLanguage, isAdmin } = useAuth();
  const { t } = useTranslation(language);
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('dashboard') },
    { path: '/properties', icon: Home, label: t('properties') },
    { path: '/services', icon: Calendar, label: t('services') },
    { path: '/messages', icon: MessageSquare, label: t('messages') },
    { path: '/settings', icon: Settings, label: t('settings') },
  ];

  if (isAdmin) {
    navItems.push({ path: '/admin', icon: Shield, label: t('admin') });
  }

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile header */}
      <header className="lg:hidden glass-header sticky top-0 z-50 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 hover:bg-accent rounded-lg transition-colors"
          data-testid="mobile-menu-btn"
        >
          <Menu className="h-6 w-6 text-foreground" />
        </button>
        
        <h1 className="font-heading font-semibold text-lg text-foreground">Conciergerie</h1>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="language-dropdown-mobile">
              <Globe className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setLanguage('en')} data-testid="lang-en-mobile">
              {t('english')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setLanguage('fr')} data-testid="lang-fr-mobile">
              {t('french')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-border
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.png" alt="3+ Conciergerie" className="h-10 w-10 object-contain" />
              <span className="font-heading font-bold text-xl text-foreground">Conciergerie</span>
            </Link>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 hover:bg-accent rounded"
              data-testid="close-sidebar-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* User info */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                <span className="font-medium text-secondary-foreground">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role === 'admin' ? t('admin_role') : t('owner')}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`sidebar-item ${isActive(item.path) ? 'sidebar-item-active' : ''}`}
                data-testid={`nav-${item.path.replace('/', '')}`}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-border space-y-2">
            {/* Language switcher - desktop */}
            <div className="hidden lg:block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="sidebar-item w-full" data-testid="language-dropdown-desktop">
                    <Globe className="h-5 w-5" />
                    <span>{t('language')}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="top">
                  <DropdownMenuItem onClick={() => setLanguage('en')} data-testid="lang-en-desktop">
                    {t('english')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLanguage('fr')} data-testid="lang-fr-desktop">
                    {t('french')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            <button 
              onClick={logout}
              className="sidebar-item w-full text-destructive hover:bg-destructive/10 hover:text-destructive"
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
              <span>{t('logout')}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-6 md:p-8 lg:p-12">
          {children}
        </div>
      </main>
    </div>
  );
};

export default MainLayout;
